import { useQuery } from '@apollo/client/react';
import kebabCase from 'lodash/kebabCase';
import { Trans, useTranslation } from 'next-i18next/pages';

import { CandidatePriority } from '@pflegia/types/api/candidate/candidate-priority';
import AvailableJobs from '@pflegia/ui/shared/available-jobs';
import ContainerSection from '@pflegia/ui/shared/container-section';
import GuestHeroImage from '@pflegia/ui/shared/guest-hero-image';
import QuestionsAndAnswers from '@pflegia/ui/shared/landing-page/questions-and-answers/QuestionsAndAnswers';

import useJobBoardSeoObjects from '@domains/job-board/hooks/use-job-board-seo-objects';
import DemandGapCluster from '@domains/landing-page/components/city-cluster/DemandGapCluster';
import Headline from '@domains/landing-page/components/headline/Headline';
import SalaryCalculatorJobsCarousel from '@domains/salary-calculator/components/SalaryCalculatorJobsCarousel';

import heroImage from '@public/assets/three-people-looking-at-documents.png';

import { JOBS, SALARY_CALCULATOR } from '@shared/application/constants';
import NextSeo from '@shared/components/NextSeo';
import { EMPLOYER_COUNT } from '@shared/constants/counts';
import { JobDetailsProvider } from '@shared/context/job-details.context';
import { GET_JOB_POSITIONS_COUNT } from '@shared/graphql/operations/job-positions-count';
import { useFaqLinks } from '@shared/hooks/use-faq-links';

import CtaBanner from './CtaBanner';
import JobsByProfession from './JobsByProfession';
import JobsSearchCard, { JOBS_SEARCH_ANCHOR_ID } from './JobsSearchCard';
import SignUpCta from './SignUpCta';
import TrustBar from './TrustBar';
import WhyPflegia from './WhyPflegia';

export const CARE_TYPES = ['hospital', 'nursingHome', 'intensive', 'dentalPractice'];

/**
 * Candidate-facing subset of the `faq` namespace. Excludes `reviews` and the employer-facing entries.
 */
export const HUB_FAQS = ['whatIsPflegia', 'areJobsReal', 'isFree', 'isAnonymous', 'howMatchingWorks', 'locations'];

const JobsPageHub: React.FC = () => {
  const { t } = useTranslation('jobBoard', { keyPrefix: 'jobsPageHub' });
  const { t: tCandidateFields } = useTranslation('candidateFields');
  const { seoObjects } = useJobBoardSeoObjects({});
  const { data } = useQuery(GET_JOB_POSITIONS_COUNT);
  const { components, processedSchemaUrls } = useFaqLinks();

  return (
    <>
      <NextSeo {...seoObjects} />
      <div className='bg-primary-100 pb-16'>
        <ContainerSection>
          <div className='flex flex-col-reverse gap-8 pt-8 pb-2 lg:flex-row lg:items-center lg:justify-between lg:pt-14'>
            <div className='w-full pr-0 text-left lg:w-7/12 xl:w-175'>
              <h1 className='mb-6 text-2xl leading-tight md:text-5xl md:whitespace-normal lg:mb-5 lg:text-[56px]'>
                <Trans components={{ span: <span className='text-primary' /> }} i18nKey='hero.title' t={t} />
              </h1>
              <p className='text-secondary mb-4 text-lg font-light md:text-xl lg:text-2xl'>{t('hero.subtitle')}</p>
            </div>

            <GuestHeroImage
              alt={t('hero.title')}
              className='mx-auto w-11/12 max-w-xl md:w-8/12 lg:mx-0 lg:max-h-132 lg:w-auto lg:min-w-130'
              src={heroImage}
              slipSvgRight
            />
          </div>

          <div className='relative z-10 xl:-mt-16'>
            <JobsSearchCard jobsCount={data?.getJobPositionCount} />
          </div>
        </ContainerSection>
      </div>

      <TrustBar />
      <JobsByProfession />

      <ContainerSection>
        <DemandGapCluster
          titleKey='jobBoard:jobsPageHub.demandGapCluster.title'
          subTitleKey='jobBoard:jobsPageHub.demandGapCluster.subtitle'
        />
      </ContainerSection>

      <CtaBanner className='mt-20 mb-32 xl:px-20' href={`#${JOBS_SEARCH_ANCHOR_ID}`} translationPrefix='jobsPageHub.nearbyJobsCta' />

      <ContainerSection>
        <AvailableJobs
          altTextShown
          cardClassName='h-full md:w-full justify-start'
          items={CARE_TYPES}
          itemsClassName='lg:grid lg:grid-cols-2'
          headline={
            <Headline
              i18nKey='landingPage:availableJobs.title'
              subTitle='landingPage:availableJobs.subtitle'
              subTitleValues={{ employerCount: EMPLOYER_COUNT }}
            />
          }
          itemLink={item => `${JOBS}/${kebabCase(tCandidateFields(`typesOfWork.${item}`))}`}
        />
      </ContainerSection>

      <ContainerSection className='mt-16 lg:mt-28'>
        <JobDetailsProvider>
          <SalaryCalculatorJobsCarousel
            filters={{ excludePriorities: [CandidatePriority.A] }}
            title={<Headline i18nKey='jobBoard:jobsPageHub.jobsCarousel.title' subTitle='jobBoard:jobsPageHub.jobsCarousel.subtitle' />}
          />
        </JobDetailsProvider>
      </ContainerSection>

      <SignUpCta />
      <WhyPflegia />
      <CtaBanner className='mb-32' href={SALARY_CALCULATOR} translationPrefix='jobsPageHub.salaryCalculatorCta' />

      <ContainerSection className='mb-32'>
        <QuestionsAndAnswers
          faqs={HUB_FAQS}
          headingText={t('faqHeading')}
          namespace='faq'
          components={{ ...components, jobsLink: <span /> }}
          processedSchemaUrls={processedSchemaUrls}
        />
      </ContainerSection>
    </>
  );
};

export default JobsPageHub;
